export type TutorialBadgeColor = 'indigo' | 'violet' | 'cyan' | 'emerald' | 'amber'
export type PlatformId = 'windows' | 'macos' | 'linux' | 'all'

export interface CodeBlock {
  label: string
  code: string
  language?: string
}

export interface Step {
  title: string
  description?: string
  codes?: CodeBlock[]
  notes?: string[]
}

export interface Platform {
  id: PlatformId
  label: string
  steps: Step[]
}

export interface TutorialTool {
  id: string
  name: string
  tagline: string
  iconKey: string
  badgeText: string
  badgeColor: TutorialBadgeColor
  gradientFrom: string
  gradientTo: string
  textColor: string
  stepColor: string
  platforms: Platform[]
  steps: Step[]
}

const pythonExample = `from openai import OpenAI

client = OpenAI(
    api_key="YOUR_API_KEY",
    base_url="https://api.tokentcp.com/v1"
)

response = client.chat.completions.create(
    model="gpt-4o",
    messages=[
        {"role": "system", "content": "You are a helpful assistant."},
        {"role": "user", "content": "给我一段 Python 快速入门建议"}
    ],
    stream=True
)

for chunk in response:
    content = chunk.choices[0].delta.content
    if content:
        print(content, end="")`

const installDisclaimer =
  '如工具官方安装方式有变化，请以对应工具官方文档为准。'

const claudeWindowsSteps: Step[] = [
  {
    title: '系统要求',
    description: 'Node.js 18+，Windows 需 PowerShell 5.1+。',
  },
  {
    title: '安装 Claude Code',
    description: `可通过 npm 全局安装，或使用 Claude Code 官方提供的其他安装方式（如平台包管理器）。${installDisclaimer}`,
    codes: [
      {
        label: 'PowerShell (npm)',
        language: 'powershell',
        code: 'npm install -g @anthropic-ai/claude-code',
      },
    ],
  },
  {
    title: '配置 Base URL / Endpoint',
    description: 'Anthropic 兼容工具使用根域名，不要追加 /v1。设置环境变量后即可通过 TokenTCP 网关调用。',
    codes: [
      {
        label: 'PowerShell',
        language: 'powershell',
        code: '$env:ANTHROPIC_BASE_URL = "https://api.tokentcp.com"\n$env:ANTHROPIC_API_KEY  = "YOUR_API_KEY"',
      },
    ],
    notes: ['API Key 请使用在 TokenTCP 控制台创建的 Key。', '建议写入 PowerShell Profile 以便长期生效。'],
  },
  {
    title: '启动',
    description: '进入工程目录后启动 Claude Code。',
    codes: [
      {
        label: 'PowerShell',
        language: 'powershell',
        code: 'claude',
      },
    ],
  },
]

const claudeUnixSteps: Step[] = [
  {
    title: '系统要求',
    description: 'Node.js 18+。',
  },
  {
    title: '安装 Claude Code',
    description: `可通过 npm 全局安装，或使用 Claude Code 官方提供的其他安装方式。${installDisclaimer}`,
    codes: [
      {
        label: 'Shell (npm)',
        language: 'bash',
        code: 'npm install -g @anthropic-ai/claude-code',
      },
    ],
  },
  {
    title: '配置 Base URL / Endpoint',
    description: 'Anthropic 兼容工具使用 https://api.tokentcp.com（根域名，不含 /v1）。',
    codes: [
      {
        label: 'Shell',
        language: 'bash',
        code: 'export ANTHROPIC_BASE_URL="https://api.tokentcp.com"\nexport ANTHROPIC_API_KEY="YOUR_API_KEY"',
      },
    ],
    notes: ['API Key 请使用在 TokenTCP 控制台创建的 Key。', '建议写入 ~/.bashrc 或 ~/.zshrc，重新打开终端后自动生效。'],
  },
  {
    title: '启动',
    codes: [
      {
        label: 'Shell',
        language: 'bash',
        code: 'claude',
      },
    ],
  },
]

const codexWindowsSteps: Step[] = [
  {
    title: '系统要求',
    description: 'Node.js 22+（推荐 LTS）。',
  },
  {
    title: '安装 Codex CLI',
    description: `可通过 standalone installer、npm 或 Homebrew 等方式安装。${installDisclaimer}`,
    codes: [
      {
        label: 'PowerShell (npm 示例)',
        language: 'powershell',
        code: 'npm install -g @openai/codex',
      },
    ],
  },
  {
    title: '配置 Base URL / Endpoint',
    description: 'OpenAI 兼容 CLI 使用带 /v1 的 Base URL。',
    codes: [
      {
        label: 'PowerShell',
        language: 'powershell',
        code: '$env:OPENAI_API_KEY  = "YOUR_API_KEY"\n$env:OPENAI_BASE_URL = "https://api.tokentcp.com/v1"',
      },
    ],
    notes: ['OPENAI_BASE_URL 需要包含 /v1。', 'API Key 请使用在 TokenTCP 控制台创建的 Key。'],
  },
  {
    title: '启动',
    codes: [
      {
        label: 'PowerShell',
        language: 'powershell',
        code: 'codex\n# 或\ncodex "描述你的任务"',
      },
    ],
  },
]

const codexUnixSteps: Step[] = [
  {
    title: '系统要求',
    description: 'Node.js 22+（推荐 LTS）。',
  },
  {
    title: '安装 Codex CLI',
    description: `可通过 standalone installer、npm 或 Homebrew 等方式安装。${installDisclaimer}`,
    codes: [
      {
        label: 'Shell (npm 示例)',
        language: 'bash',
        code: 'npm install -g @openai/codex',
      },
    ],
  },
  {
    title: '配置 Base URL / Endpoint',
    description: 'OpenAI 兼容 CLI 使用 https://api.tokentcp.com/v1。',
    codes: [
      {
        label: 'Shell',
        language: 'bash',
        code: 'export OPENAI_API_KEY="YOUR_API_KEY"\nexport OPENAI_BASE_URL="https://api.tokentcp.com/v1"',
      },
    ],
    notes: ['Codex CLI 使用 OPENAI_BASE_URL，路径末尾必须包含 /v1。', 'API Key 请使用在 TokenTCP 控制台创建的 Key。'],
  },
  {
    title: '启动',
    codes: [
      {
        label: 'Shell',
        language: 'bash',
        code: 'codex\n# 或\ncodex "描述你的任务"',
      },
    ],
  },
]

export const tutorialTools: TutorialTool[] = [
  {
    id: 'python-sdk',
    name: 'Python SDK',
    tagline: '兼容 OpenAI 官方 SDK，仅需替换 base_url',
    iconKey: 'python',
    badgeText: 'SDK',
    badgeColor: 'indigo',
    gradientFrom: 'from-indigo-50',
    gradientTo: 'to-cyan-50',
    textColor: 'text-indigo-900',
    stepColor: 'bg-indigo-600',
    platforms: [],
    steps: [
      {
        title: '注册并创建 API Key',
        description:
          '注册后进入控制台创建 API Key，并按已开通额度使用模型服务。控制台 -> API Keys -> 创建，按需配置分组、模型限制与额度策略。',
      },
      {
        title: '安装依赖',
        codes: [
          {
            label: 'Shell',
            language: 'bash',
            code: 'pip install openai',
          },
        ],
      },
      {
        title: '发起调用',
        description: '直接复用 OpenAI SDK 代码，只需替换 base_url。',
        codes: [
          {
            label: 'example.py',
            language: 'python',
            code: pythonExample,
          },
        ],
        notes: [
          '模型标签示例：gpt-4o、claude-sonnet-4-5、gemini-2.0-flash、deepseek-chat。',
          '如果需要分组限流，可在创建 Key 时绑定指定分组。',
        ],
      },
    ],
  },
  {
    id: 'claude-code',
    name: 'Claude Code',
    tagline: 'Anthropic 官方 AI 编程 CLI，接入 TokenTCP 后可按量使用',
    iconKey: 'claude',
    badgeText: 'CLI',
    badgeColor: 'violet',
    gradientFrom: 'from-violet-50',
    gradientTo: 'to-indigo-50',
    textColor: 'text-violet-900',
    stepColor: 'bg-violet-600',
    platforms: [
      { id: 'windows', label: 'Windows', steps: claudeWindowsSteps },
      { id: 'macos', label: 'macOS', steps: claudeUnixSteps },
      { id: 'linux', label: 'Linux', steps: claudeUnixSteps },
    ],
    steps: [],
  },
  {
    id: 'cc-switch',
    name: 'CC Switch',
    tagline: '统一管理 Claude Code、Codex、Gemini CLI 等工具的 Provider 配置',
    iconKey: 'ccSwitch',
    badgeText: 'CLI 管理',
    badgeColor: 'cyan',
    gradientFrom: 'from-cyan-50',
    gradientTo: 'to-indigo-50',
    textColor: 'text-cyan-900',
    stepColor: 'bg-cyan-600',
    platforms: [],
    steps: [
      {
        title: '安装或打开 CC Switch',
        description:
          '从官方 Releases 安装桌面端，或按团队提供的安装方式启动 CC Switch。',
      },
      {
        title: '新增 TokenTCP Provider',
        description:
          '在 CC Switch 中新增 Provider，按目标工具类型填写 Base URL 与 API Key。',
        codes: [
          {
            label: 'OpenAI-compatible tools',
            language: 'bash',
            code: 'Base URL: https://api.tokentcp.com/v1\nAPI Key: <your-tokentcp-api-key>',
          },
          {
            label: 'Claude Code / Anthropic-compatible tools',
            language: 'bash',
            code: 'ANTHROPIC_BASE_URL=https://api.tokentcp.com\nANTHROPIC_AUTH_TOKEN=<your-tokentcp-api-key>',
          },
        ],
        notes: [
          'OpenAI Compatible / Codex 类工具：Base URL 填写 https://api.tokentcp.com/v1。',
          'Anthropic / Claude Code 类工具：Base URL 填写 https://api.tokentcp.com。',
          'API Key 填写在 TokenTCP 控制台创建的 Key。',
          '模型名填写平台中已开通且该 Key 有权限访问的模型。',
        ],
      },
      {
        title: '选择目标工具并切换 Provider',
        description:
          '选择 Claude Code、Codex、Gemini CLI、OpenCode 等目标工具，将 Provider 切换为 TokenTCP，然后重新打开终端或对应 CLI 进行验证。',
      },
    ],
  },
  {
    id: 'codex-cli',
    name: 'Codex CLI',
    tagline: 'OpenAI 官方代码助手 CLI，支持工程级任务',
    iconKey: 'codex',
    badgeText: 'CLI',
    badgeColor: 'violet',
    gradientFrom: 'from-violet-50',
    gradientTo: 'to-cyan-50',
    textColor: 'text-violet-900',
    stepColor: 'bg-violet-600',
    platforms: [
      { id: 'windows', label: 'Windows', steps: codexWindowsSteps },
      { id: 'macos', label: 'macOS', steps: codexUnixSteps },
      { id: 'linux', label: 'Linux', steps: codexUnixSteps },
    ],
    steps: [],
  },
  {
    id: 'cursor',
    name: 'Cursor',
    tagline: 'AI 增强代码编辑器，替换 API 端点即可使用本平台模型',
    iconKey: 'cursor',
    badgeText: 'App',
    badgeColor: 'cyan',
    gradientFrom: 'from-cyan-50',
    gradientTo: 'to-indigo-50',
    textColor: 'text-cyan-900',
    stepColor: 'bg-cyan-600',
    platforms: [],
    steps: [
      {
        title: '打开设置',
        description: 'Cursor -> Settings（Cmd/Ctrl + ,）-> Models。',
      },
      {
        title: '填写 API 信息',
        description: 'API Provider 选择 OpenAI Compatible。',
        notes: [
          'API Key: 填入在 TokenTCP 控制台创建的 API Key。',
          'Base URL: https://api.tokentcp.com/v1。',
        ],
      },
      {
        title: '选择模型',
        description: '在模型列表中输入目标模型名称，例如 gpt-4o、claude-sonnet-4-5。',
      },
    ],
  },
  {
    id: 'cherry-studio',
    name: 'Cherry Studio',
    tagline: '功能丰富的桌面 AI 客户端，支持多种模型提供商',
    iconKey: 'cherryStudio',
    badgeText: 'App',
    badgeColor: 'emerald',
    gradientFrom: 'from-emerald-50',
    gradientTo: 'to-cyan-50',
    textColor: 'text-emerald-900',
    stepColor: 'bg-emerald-600',
    platforms: [],
    steps: [
      {
        title: '打开设置',
        description: '左侧栏底部设置图标 -> 模型服务 -> 添加服务商。',
      },
      {
        title: '配置提供商',
        notes: [
          '服务商类型选择 OpenAI Compatible。',
          'API Base URL 填写 https://api.tokentcp.com/v1。',
          'API Key 填写在 TokenTCP 控制台创建的 Key。',
        ],
      },
      {
        title: '添加模型',
        description: '点击“获取模型列表”或手动输入模型 ID，保存后即可在对话中使用。',
      },
    ],
  },
  {
    id: 'chatbox',
    name: 'ChatBox',
    tagline: '轻量级跨平台 AI 对话工具',
    iconKey: 'chatbox',
    badgeText: 'App',
    badgeColor: 'amber',
    gradientFrom: 'from-amber-50',
    gradientTo: 'to-orange-50',
    textColor: 'text-amber-900',
    stepColor: 'bg-amber-600',
    platforms: [],
    steps: [
      {
        title: '打开设置',
        description: '右上角设置 -> AI 提供商。',
      },
      {
        title: '添加 OpenAI 兼容提供商',
        notes: [
          '选择 OpenAI API。',
          'API Host 设置为 https://api.tokentcp.com/v1。',
          '填入在 TokenTCP 控制台创建的 API Key。',
        ],
      },
      {
        title: '选择模型',
        description: '在模型下拉中选择目标模型后即可开始对话。',
      },
    ],
  },
]
